import { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { ErrorApiResponse } from '@/types/api.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  AdvertiseListQueryParams,
  IAdvertiseData,
} from '@/types/advertise-management.types';
import {
  useDeleteAdvertiseMutation,
  useGetAdvertisesQuery,
  useGetSlotsQuery,
} from '@/services/AdvertiseManagementService';
import { formatDisplayDate } from '@/utils/Date';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { CampaignStatus } from '@/constant';

interface SlotOption {
  value: string;
  label: string;
}

//

export const AdvertiseListings = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    advertiseId: string | null;
  }>({ isOpen: false, advertiseId: null });

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedSlot, setSelectedSlot] = useState<SlotOption | null>(null);
  const [slotOptions, setSlotOptions] = useState<SlotOption[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteAdvertise] = useDeleteAdvertiseMutation();

  const formatCampaignDate = useCallback((isoDate?: string | null) => {
    if (!isoDate) return '';
    return formatDisplayDate(isoDate, '');
  }, []);

  // Fetch slots for filter dropdown
  const { data: slotsData } = useGetSlotsQuery();

  useEffect(() => {
    if (slotsData?.data) {
      const options = slotsData.data.map((slot) => ({
        value: slot.id,
        label: `${slot.name} - ${slot.position}`,
      }));
      setSlotOptions(options);
    }
  }, [slotsData]);

  // Query parameters for API
  const queryParams: AdvertiseListQueryParams = useMemo(() => {
    const params: AdvertiseListQueryParams = {
      page: pagination.pageIndex + 1, // Convert 0-based to 1-based
      limit: pagination.pageSize,
    };

    if (debouncedValue) params.search = debouncedValue;
    if (selectedSlot) params.slot_id = selectedSlot.value;
    if (statusFilter !== 'all' && statusFilter) {
      params.status = statusFilter as CampaignStatus;
    }

    return params;
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedValue,
    selectedSlot,
    statusFilter,
  ]);

  // Fetch advertisements
  const { data: advertisesData, isLoading } =
    useGetAdvertisesQuery(queryParams);

  useEffect(() => {
    if (advertisesData) {
      handleTotalPages(
        advertisesData.totalPages || advertisesData.total_pages || 1
      );
      handleTotalItems(
        advertisesData.totalItems || advertisesData.total_items || 0
      );
    }
  }, [advertisesData, handleTotalPages, handleTotalItems]);

  // Route-based view/edit; no modal state handlers needed

  const handleDelete = useCallback(
    async (advertiseId: string) => {
      try {
        await deleteAdvertise(advertiseId).unwrap();
        enqueueSnackbar('Advertisement deleted successfully', {
          variant: 'success',
        });
        setConfirmDeleteState({ isOpen: false, advertiseId: null });
      } catch (err) {
        const errorMessage =
          (err as ErrorApiResponse)?.data?.message ||
          'Failed to delete advertisement';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    [deleteAdvertise, enqueueSnackbar]
  );

  const handleSlotFilterChange = useCallback(
    (selectedOption: SlotOption | null) => {
      setSelectedSlot(selectedOption);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setPagination]
  );

  const handleStatusFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setPagination]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'ad_name',
        header: 'Advertisement Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: { data: IAdvertiseData } } }) => (
          <button
            type="button"
            onClick={() =>
              navigate(`/advertise/edit/${row.original.data.id}?mode=view`)
            }
            className="flex flex-col items-start text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-sm"
            aria-label={`View ${row.original.data.ad_name || row.original.data.name || 'advertisement'}`}
          >
            <span className="text-sm font-medium text-gray-900 hover:text-primary-active">
              {row.original.data.ad_name || row.original.data.name || ''}
            </span>
          </button>
        ),
      },
      {
        accessorKey: 'start_date',
        header: 'Start Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: { data: IAdvertiseData } } }) => (
          <span className="text-sm text-gray-900">
            {formatCampaignDate(row.original.data.start_date)}
          </span>
        ),
      },
      {
        accessorKey: 'end_date',
        header: 'End Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: { data: IAdvertiseData } } }) => (
          <span className="text-sm text-gray-900">
            {formatCampaignDate(row.original.data.end_date)}
          </span>
        ),
      },
      {
        accessorKey: 'target_url',
        header: 'Target URL',
        enableSorting: false,
        cell: ({ row }: { row: { original: { data: IAdvertiseData } } }) => (
          <a
            href={row.original.data.target_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 contrast:text-blue-200 hover:text-blue-800 truncate max-w-xs focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-sm"
          >
            {row.original.data.target_url}
          </a>
        ),
      },
      {
        accessorKey: 'image',
        header: 'Image',
        enableSorting: false,
        cell: ({ row }: { row: { original: { data: IAdvertiseData } } }) => (
          <div className="flex items-center">
            {(() => {
              const imgSrc =
                (typeof row.original.data.image === 'string'
                  ? row.original.data.image
                  : undefined) || row.original.data.image_url;
              return imgSrc ? (
                <img
                  src={imgSrc}
                  alt="Advertisement"
                  className="w-12 h-12 object-cover rounded border"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded border flex items-center justify-center">
                  <KeenIcon icon="image" className="text-gray-400" />
                </div>
              );
            })()}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: { data: IAdvertiseData } } }) => {
          const data = row.original.data;
          const rawStatus = data.status;

          let label = '';
          let badgeClass = 'bg-gray-100 text-gray-800';

          // 1) Prefer string status coming from API (e.g., "draft", "active")
          if (typeof rawStatus === 'string') {
            const s = rawStatus.toLowerCase();
            label = s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
            if (['active', 'published', 'live'].includes(s)) {
              badgeClass = 'bg-green-100 text-green-800';
            } else if (['inactive', 'disabled'].includes(s)) {
              badgeClass = 'bg-red-100 text-red-800';
            } else if (['draft', 'pending'].includes(s)) {
              badgeClass = 'bg-yellow-100 text-yellow-800';
            } else if (['paused'].includes(s)) {
              badgeClass = 'bg-orange-100 text-orange-800';
            }
          }
          // 2) Fall back to is_active boolean
          else if (typeof data.is_active === 'boolean') {
            label = data.is_active ? 'Active' : 'Inactive';
            badgeClass = data.is_active
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800';
          }
          // 3) Fall back to numeric status
          else if (typeof rawStatus === 'number') {
            label = rawStatus === 1 ? 'Active' : 'Inactive';
            badgeClass =
              rawStatus === 1
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800';
          }
          // 4) Unknown status
          else {
            label = 'Unknown';
          }

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: { data: IAdvertiseData } } }) => {
          const advertise = row.original.data;
          return (
            <div className="flex items-center gap-2">
              {hasPermission('advertisement_management_read') && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/advertise/edit/${advertise.id}?mode=view`)
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="View"
                  aria-label={`View ${advertise.ad_name || advertise.name || 'advertisement'}`}
                >
                  <KeenIcon type="filled" icon="eye" className="text-gray-500" />
                </button>
              )}
              {hasPermission('advertisement_management_update') && (
                <button
                  type="button"
                  onClick={() => navigate(`/advertise/edit/${advertise.id}`)}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Edit"
                  aria-label={`Edit ${advertise.ad_name || advertise.name || 'advertisement'}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
              )}
              {hasPermission('advertisement_management_delete') && (
                <button
                  type="button"
                  onClick={() =>
                    setConfirmDeleteState({
                      isOpen: true,
                      advertiseId: advertise.id,
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Delete"
                  aria-label={`Delete ${advertise.ad_name || advertise.name || 'advertisement'}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="trash"
                    className="text-gray-500"
                  />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [hasPermission, navigate, formatCampaignDate]
  );

  const data = useMemo(() => {
    return (
      advertisesData?.data?.map((advertise) => ({ data: advertise })) || []
    );
  }, [advertisesData]);

  const resetFilters = useCallback(() => {
    if (searchTerm && searchTerm.trim() !== '') resetSearch();
    setSelectedSlot(null);
    setStatusFilter('all');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchTerm, resetSearch, setPagination]);

  return (
    <section role="region" aria-label="Advertisement listings">
      <div className="advertise-listings card card-grid w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <div className="flex-shrink-0" role="search">
              {/* Search */}
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-advertise"
                aria-label="Search advertisements by name"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                />
                <input
                  id="search-advertise"
                  placeholder="Search advertisements"
                  type="text"
                  value={searchTerm || ''}
                  onChange={handleSearch}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search advertisements by name"
                  disabled={isLoading}
                />
              </label>
            </div>

            {/* Slot Filter */}
            <div className="flex-shrink-0 w-full sm:w-64">
                <Select
                  value={selectedSlot}
                  onChange={handleSlotFilterChange}
                  options={slotOptions}
                  placeholder="Filter by slot..."
                  isClearable
                  className="text-sm"
                  classNamePrefix="react-select"
                  aria-label="Filter by slot"
                  isDisabled={isLoading}
                />
            </div>

            <div className="flex-shrink-0">
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
                aria-label="Filter by status"
                title="Filter by status"
                disabled={isLoading}
              >
                <option value="all">All Statuses</option>
                <option value={CampaignStatus.DRAFT}>Draft</option>
                <option value={CampaignStatus.ACTIVE}>Active</option>
                <option value={CampaignStatus.PAUSED}>Paused</option>
                <option value={CampaignStatus.COMPLETED}>Completed</option>
                <option value={CampaignStatus.CANCELLED}>Cancelled</option>
              </select>
            </div>
          </div>

          <div
            className="flex items-center gap-2"
            role="toolbar"
            aria-label="Filter controls"
          >
            {(searchTerm && searchTerm.trim() !== '') ||
            selectedSlot ||
            statusFilter !== 'all' ? (
              <button
                type="button"
                data-testid="reset-filters"
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={resetFilters}
                aria-label="Clear all filters"
                title="Clear all filters"
                disabled={isLoading}
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                  aria-hidden="true"
                />
                Clear Filters
              </button>
            ) : null}
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div
              className="text-center py-4"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <KeenIcon
                type="filled"
                icon="spinner"
                className="text-gray-500 text-2xl animate-spin"
                aria-hidden="true"
              />
              <span className="ml-2 text-gray-700">Loading...</span>
            </div>
          ) : (
            <>
              {!data.length && (
                <div className="text-center py-4">
                  <p className="text-gray-500">No advertisements available</p>
                </div>
              )}
              <DataGrid
                columns={columns}
                data={data}
                rowSelect={false}
                loading={isLoading}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalPages: totalPages,
                  totalItems: totalItems,
                  setPagination: setPagination,
                  info: '{from} - {to} of {count}',
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                serverSide={true}
                onFilterReset={resetFilters}
                hasActiveFilters={
                  !!(searchTerm && searchTerm.trim() !== '') ||
                  !!selectedSlot ||
                  statusFilter !== 'all'
                }
                aria-label="Advertisement listings table"
              />
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDeleteState.isOpen}
        onClose={() =>
          setConfirmDeleteState({ isOpen: false, advertiseId: null })
        }
        onConfirm={() => {
          if (confirmDeleteState.advertiseId) {
            handleDelete(confirmDeleteState.advertiseId);
          }
        }}
        message="Are you sure you want to delete this advertisement? This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        confirmButtonVariant="destructive"
      />
    </section>
  );
};
